import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@tinct/web-components';
import { CreatedServiceAccount, ServiceAccount } from '@tinct/core-api-models';
import { useRotateServiceAccountSecret } from '@tinct/core-api';
import { IconAlertTriangle } from '@tabler/icons-react';
import { toast } from 'sonner';
import { RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { ServiceAccountSecretReveal } from '../components/service-account-secret-reveal';

type RotateServiceAccountSecretModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  serviceAccount: ServiceAccount;
};

export const RotateServiceAccountSecretModal = ({
  open,
  onOpenChange,
  organizationId,
  serviceAccount,
}: RotateServiceAccountSecretModalProps) => {
  const [rotatedServiceAccount, setRotatedServiceAccount] =
    useState<CreatedServiceAccount | null>(null);

  const rotateSecret = useRotateServiceAccountSecret({
    onSuccess: (rotated) => {
      setRotatedServiceAccount(rotated);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to rotate secret');
    },
  });

  const handleRotate = () => {
    if (!serviceAccount.id) return;
    rotateSecret.mutate({
      organizationId,
      serviceAccountId: serviceAccount.id,
    });
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setRotatedServiceAccount(null);
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        {rotatedServiceAccount ? (
          <>
            <DialogHeader>
              <DialogTitle>New secret generated</DialogTitle>
            </DialogHeader>

            <ServiceAccountSecretReveal
              clientId={rotatedServiceAccount.clientId ?? ''}
              clientSecret={rotatedServiceAccount.clientSecret ?? ''}
            />

            <DialogFooter>
              <Button
                type="button"
                className="w-full"
                onClick={() => handleOpenChange(false)}
              >
                Done
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Rotate secret</DialogTitle>
            </DialogHeader>

            <div className="flex gap-3 items-start rounded-lg border border-rose-200 bg-rose-50 px-4 py-3">
              <div className="pt-0.5">
                <IconAlertTriangle className="size-4 text-destructive" />
              </div>
              <div className="flex flex-col gap-1 flex-1 text-destructive">
                <p className="text-sm font-medium">
                  Service account: {serviceAccount.name}
                </p>
                <p className="text-sm">
                  The current secret stops working immediately. Any integration
                  using it will break until it is updated with the new secret.
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={handleRotate}
                isLoading={rotateSecret.isPending}
              >
                <RefreshCw className="size-4" />
                Rotate secret
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
